<template>
    <Attr :widget_id="widget_id" :attr_id="attr_id">
        <!-- IE 2026 10 16 / ODOO-5800 (ЗП-20261016-2, поручение Игоря 16.10): граф связей статусов через причины над гридом вкладки Transitions.
             Узлы = статусы, рёбра = причины (строки грида); клик по ребру выделяет строку грида (gridName) и подсвечивает ребро; выделение строки грида
             подсвечивает её ребро. Геометрия — utils/transitionsGraph.js; данные — attr.binding (граф бэка) либо attr.rowsBinding (строки грида).
             Выключатель fe.ux.transitionsGraph (дефолт ВКЛ; выкл — только грид).
             IE 2026 10 16 / ODOO-5800 (уровни): узел «In review» — по уровню согласования («In review → Approved by PM», expandLevels); копии ребра без
             уровня (key) несут тот же id строки — клик / выделение по id
             IE 2026 10 17 / ODOO-5800 (graph layout) (поручение Игоря 17.10): узлы перетаскиваются мышью / тачем (pointer events; рёбра пересчитываются на лету; тяга < 4 px —
             не перетаскивание); отпустили — расстановка ВСЕХ узлов пишется в поле записи doc.approval.graph_layout (store.attr_set, как любое поле формы —
             загорается Save; сохранение штатное), при открытии вкладки применяется она; кнопка «Default layout» очищает поле ({}) — авторасстановка -->
        <div class="attr-trgraph" ref="host" v-if="on" v-wjTooltip="attr.tooltip" @mouseenter="hookGrid">
            <div class="trgraph-head">
                <span class="trgraph-title">{{ t('Status transitions') }}</span>
                <span class="trgraph-legend">
                    <span class="trgraph-lg trg-primary">{{ t('Forward') }}</span>
                    <span class="trgraph-lg trg-back">{{ t('Back') }}</span>
                    <span class="trgraph-lg trg-danger">{{ t('Cancel') }}</span>
                    <span class="trgraph-lg trg-system">{{ t('System') }}</span>
                </span>
                <span class="trgraph-hint">{{ t('Click an edge to select its reason row') }} · {{ t('Drag a node to move it') }}</span>
                <!-- IE 2026 10 17 / ODOO-5800 (graph layout): сброс к авторасстановке (поле graph_layout → {}) -->
                <button type="button" class="trgraph-btn" v-if="model && (savedLayout || dragLayout)" :title="t('Reset the nodes to the automatic layout and clear the saved one')" @click.stop="resetLayout">
                    <i class="pi pi-sitemap"></i><span>{{ t('Default layout') }}</span></button>
            </div>
            <div class="trgraph-empty" v-if="!model">{{ t('No transitions yet') }}</div>
            <svg v-else ref="svg" class="trgraph-svg" :class="{ 'trg-dragging': !!drag }" :viewBox="(model.x0 || 0) + ' ' + (model.y0 || 0) + ' ' + model.W + ' ' + model.H" :style="{ height: svgHeight + 'px' }" width="100%" preserveAspectRatio="xMinYMin meet" @mouseleave="hover = null">
                <g v-for="e in edgesOrdered" :key="'e' + e.key" class="trgraph-edge" :class="edgeClass(e)" :data-id="e.id"
                    @click.stop="pick(e.id)" @mouseenter="onHover(e, $event)" @mousemove="onMove($event)" @mouseleave="hover = null">
                    <path class="trg-hit" :d="e.d" />
                    <path class="trg-line" :d="e.d" />
                    <polygon class="trg-arrow" :points="e.ax" />
                </g>
                <!-- подписи — отдельным слоем поверх всех линий (иначе широкая зона клика позднего ребра накрывает подпись раннего); каждая подпись — своё ребро.
                     Подписи системных рёбер (их много: Migrated, Dimensions changed, …) — только у выделенного / под курсором, иначе граф не прочесть -->
                <g v-for="e in edgesOrdered" :key="'l' + e.key" class="trgraph-edge trgraph-edge-label" :class="edgeClass(e)" :data-id="e.id"
                    @click.stop="pick(e.id)" @mouseenter="onHover(e, $event)" @mousemove="onMove($event)" @mouseleave="hover = null">
                    <text class="trg-label" v-if="labelOn(e)" :x="e.lx" :y="e.ly" text-anchor="middle" dominant-baseline="middle">{{ shortLabel(e.title) }}</text>
                </g>
                <!-- IE 2026 10 17 / ODOO-5800 (graph layout): узел тянется pointer-событиями (захват указателя — движение за пределами узла тоже наше) -->
                <g v-for="nd in model.nodes" :key="'n' + nd.id" class="trgraph-node" :class="{ 'trg-side': nd.side, 'trg-hl': nodeLit(nd.id), 'trg-level': !!nd.lines, 'trg-drag': drag && drag.id === nd.id }" :data-id="nd.id"
                    @pointerdown="dragStart(nd, $event)" @pointermove="dragMove($event)" @pointerup="dragEnd($event)" @pointercancel="dragEnd($event)">
                    <rect :x="nd.x - nd.w / 2" :y="nd.y - nd.h / 2" :width="nd.w" :height="nd.h" rx="6" ry="6" />
                    <title>{{ nd.title }}</title>
                    <!-- узел уровня (review@k) — две строки: статус и «→ Approved by PM» -->
                    <text v-if="nd.lines" :x="nd.x" :y="nd.y" text-anchor="middle" dominant-baseline="middle">
                        <tspan :x="nd.x" :dy="-6.5">{{ shortLabel(nd.lines[0], 18) }}</tspan>
                        <tspan :x="nd.x" :dy="13" class="trg-node-sub">{{ shortLabel(nd.lines[1], 19) }}</tspan>
                    </text>
                    <text v-else :x="nd.x" :y="nd.y" text-anchor="middle" dominant-baseline="middle">{{ shortLabel(nd.title, 18) }}</text>
                </g>
            </svg>
            <!-- карточка наведения: полная подпись причины, статусы, роли -->
            <div class="trgraph-tip" v-if="hover" :style="tipStyle">
                <div class="trgraph-tip-title">{{ hover.title || hover.id }}</div>
                <div class="trgraph-tip-row">{{ nodeTitle(hover.from) }} → {{ nodeTitle(hover.to) }}</div>
                <div class="trgraph-tip-row" v-if="hover.roles"><span class="trgraph-tip-k">{{ t('Who may apply') }}:</span> {{ hover.roles }}</div>
                <div class="trgraph-tip-row" v-if="hover.edit_roles"><span class="trgraph-tip-k">{{ t('Who may edit after') }}:</span> {{ hover.edit_roles }}</div>
                <div class="trgraph-tip-row trgraph-tip-muted" v-if="hover.system">{{ t('System transition') }}</div>
                <div class="trgraph-tip-row trgraph-tip-muted" v-if="!hover.use">{{ t('Switched off') }}</div>
            </div>
        </div>
    </Attr>
</template>

<script>
// IE 2026 10 16 / ODOO-5800 (ЗП-20261016-2, поручение Игоря 16.10): граф переходов вкладки Transitions — см. шаблон и utils/transitionsGraph.js
import { useMainStore } from '@/stores/mainStore'
import { t } from '@/i18n'
import { transitionsGraphOn, pickGraph, layoutGraph, shortLabel, LAYOUT_BINDING, normalizeLayout, layoutOf, layoutKey, svgScale } from '@/utils/transitionsGraph'   // IE 2026 10 17 / ODOO-5800 (graph layout)
import { approvalStatusTitle } from '@/utils/approval'

export default {
    name: 'AttrTransitionsGraph',
    props: ['widget_id', 'attr_id'],
    data: () => ({ WIDGET: {}, attr: {}, width: 0, selectedId: '', hover: null, tipPos: { x: 0, y: 0 }, drag: null, dragLayout: null }),   // IE 2026 10 17 / ODOO-5800 (graph layout): drag — тянем узел; dragLayout — живые позиции на время тяги
    setup() { return { store: useMainStore() } },
    created() {
        this.WIDGET = this.store.findWidget(this.widget_id).WIDGET
        this.content_Changed()
    },
    mounted() {
        this.measure()
        try { this._ro = new ResizeObserver(() => this.measure()); if (this.$refs.host) this._ro.observe(this.$refs.host) } catch (e) { /* нет ResizeObserver */ }
        this._onResize = () => this.measure()
        window.addEventListener('resize', this._onResize)
        // грид монтируется той же вкладкой — цепляемся к его выделению с повторами (flex появляется после первого кадра)
        this._hookT = [0, 300, 1200].map(ms => setTimeout(() => this.hookGrid(), ms))
    },
    unmounted() {
        if (this._ro) try { this._ro.disconnect() } catch (e) { /* уже отключён */ }
        if (this._onResize) window.removeEventListener('resize', this._onResize)
        for (const t_ of this._hookT || []) clearTimeout(t_)
        this.unhookGrid()
    },
    computed: {
        on() { return transitionsGraphOn((k, d) => this.store.setting(k, d)) },
        graphDoc() {
            const [doc, binding] = this.store.attr_get_link(this.WIDGET, this.attr) || []
            const g = (doc || {})[binding]
            return g && typeof g === 'object' ? g : null
        },
        rows() {
            const rb = this.attr?.rowsBinding
            if (!rb) return []
            const [doc, binding] = this.store.attr_get_link(this.WIDGET, { binding: rb }) || []
            const data = (doc || {})[binding]
            return Array.isArray(data) ? data : []
        },
        graph() { return pickGraph(this.graphDoc, this.rows, (st) => approvalStatusTitle(st)) },
        // IE 2026 10 17 / ODOO-5800 (graph layout): поле записи с расстановкой — attr.layoutBinding из view бэка (approval_views.TRANSITIONS_LAYOUT_BINDING), иначе doc.approval.graph_layout
        layoutBinding() { return (typeof this.attr?.layoutBinding === 'string' && this.attr.layoutBinding) || LAYOUT_BINDING },
        // IE 2026 10 17 / ODOO-5800 (graph layout): сохранённая расстановка — поле записи политики (пусто → null = авто)
        savedLayout() {
            const [doc, binding] = this.store.attr_get_link(this.WIDGET, { binding: this.layoutBinding }) || []
            return normalizeLayout((doc || {})[binding])
        },
        model() {
            if (!this.on) return null
            const g = this.graph
            if (!g || !g.edges?.length) return null
            return layoutGraph(g, this.width || 800, { layout: this.dragLayout || this.savedLayout })   // IE 2026 10 17 / ODOO-5800 (graph layout): на время тяги — живые позиции
        },
        // выделенное / наведённое ребро — последним в DOM (поверх остальных)
        edgesOrdered() {
            const list = this.model?.edges || [], top = (e) => (e.id === this.selectedId ? 2 : (this.hover && e.id === this.hover.id ? 1 : 0))
            return [...list].sort((a, b) => top(a) - top(b))
        },
        svgHeight() {
            const m = this.model
            if (!m) return 0
            const w = this.width || m.W
            return Math.round(m.H * Math.min(1, w / m.W))
        },
        tipStyle() { return { left: Math.min(this.tipPos.x + 14, Math.max(0, (this.width || 400) - 300)) + 'px', top: (this.tipPos.y + 12) + 'px' } },
    },
    watch: {
        rows() { this.$nextTick(() => this.hookGrid()) },
        selectedId(v) { if (v && this.hover && this.hover.id !== v) this.hover = null },
    },
    methods: {
        t,
        shortLabel,
        content_Changed() { this.store.widget_attr_set(this) },
        measure() {
            const host = this.$refs.host
            if (!host) return
            const w = Math.floor(host.getBoundingClientRect().width)
            if (w > 0 && Math.abs(w - this.width) > 1) this.width = w
        },
        edgeClass(e) {
            return ['trg-' + (e.kind || 'plain'), { 'trg-system': e.system, 'trg-off': !e.use, 'trg-loop': e.loop, 'trg-sel': this.selectedId && e.id === this.selectedId, 'trg-dim': this.selectedId && e.id !== this.selectedId }]
        },
        labelOn(e) { return !e.system || e.id === this.selectedId || (this.hover && this.hover.id === e.id) },
        nodeLit(id) {
            if (!this.selectedId || !this.model) return false
            return this.model.edges.some(e => e.id === this.selectedId && (e.from === id || e.to === id))   // все копии ребра (узлы уровня)
        },
        nodeTitle(id) { const nd = this.model?.nodes?.find(x => x.id === id); return nd ? nd.title : (id || '') },
        onHover(e, ev) { if (this.drag) return; this.hover = e; this.onMove(ev) },   // IE 2026 10 17 / ODOO-5800 (graph layout): во время тяги карточка не нужна
        // IE 2026 10 17 / ODOO-5800 (graph layout): перетаскивание узла — pointer events с захватом; позиция в единицах viewBox (масштаб по ширине svg)
        dragStart(nd, ev) {
            if (!ev || (ev.button != null && ev.button !== 0) || !this.model) return
            const svg = this.$refs.svg, k = svgScale(this.model, svg ? svg.getBoundingClientRect().width : 0)
            this.drag = { id: nd.id, x0: nd.x, y0: nd.y, cx: ev.clientX, cy: ev.clientY, k, moved: false, pointerId: ev.pointerId }
            this.hover = null
            try { ev.currentTarget.setPointerCapture(ev.pointerId) } catch (e) { /* нет захвата — тянем, пока указатель над узлом */ }
            ev.preventDefault()
        },
        dragMove(ev) {
            const d = this.drag
            if (!d || !ev || (d.pointerId != null && ev.pointerId !== d.pointerId)) return
            const dx = ev.clientX - d.cx, dy = ev.clientY - d.cy
            if (!d.moved && Math.hypot(dx, dy) < 4) return   // тяга короче 4 px — не перетаскивание (клик)
            d.moved = true
            const base = this.dragLayout || layoutOf(this.model)   // первая тяга: все узлы — с текущих мест (расстановка не зависит от ширины)
            this.dragLayout = { ...base, [layoutKey(d.id)]: { x: Math.round((d.x0 + dx * d.k) * 10) / 10, y: Math.round((d.y0 + dy * d.k) * 10) / 10 } }
            ev.preventDefault()
        },
        dragEnd(ev) {
            const d = this.drag
            if (!d) return
            if (ev && ev.currentTarget && d.pointerId != null) try { ev.currentTarget.releasePointerCapture(d.pointerId) } catch (e) { /* захвата не было */ }
            this.drag = null
            if (!d.moved || !this.dragLayout) { this.dragLayout = null; return }
            const lay = this.dragLayout
            this.dragLayout = null
            this.saveLayout(lay)   // поле записи → загорается Save
        },
        // запись расстановки в doc.approval.graph_layout — как любое поле формы (store.attr_set: widget-row / запись, перевод не нужен)
        saveLayout(lay) {
            try { this.store.attr_set(this.WIDGET, { binding: this.layoutBinding, title: 'Graph layout' }, lay) } catch (e) { console.warn('transitions graph: layout not saved', e) }
            this.$nextTick(() => this.measure())
        },
        resetLayout() { this.dragLayout = null; this.saveLayout({}) },
        onMove(ev) {
            const host = this.$refs.host
            if (!host || !ev) return
            const r = host.getBoundingClientRect()
            this.tipPos = { x: ev.clientX - r.left, y: ev.clientY - r.top }
        },
        // грид вкладки (attr.gridName) — смонтированный AttrGrid с wijmo FlexGrid
        gridVue() {
            try {
                const name = this.attr?.gridName || this.attr?.rowsBinding
                const v = name ? this.WIDGET?.vueObj?.get_vueObj?.(name) : null
                return v && v.flex ? v : null
            } catch (e) { return null }
        },
        // клик по ребру: выделить строку грида с этим id (select + scrollIntoView) и подсветить ребро
        pick(id) {
            this.selectedId = id
            const g = this.gridVue(), flex = g?.flex
            if (!flex?.rows) return false
            for (let r = 0; r < flex.rows.length; r++) {
                const it = flex.rows[r].dataItem
                if (it && String(it.id) === String(id)) {
                    try { g.navRow_reveal?.(r) } catch (e) { /* грид в перестройке */ }
                    try { flex.select(r, Math.max(0, flex.selection?.col ?? 0)) } catch (e) { /* грид в перестройке */ }
                    try { flex.scrollIntoView(r, 0) } catch (e) { /* грид в перестройке */ }
                    return true
                }
            }
            return false
        },
        // выделение строки грида → подсветка её ребра
        hookGrid() {
            const g = this.gridVue(), flex = g?.flex
            if (!flex || this._flex === flex) return
            this.unhookGrid()
            this._flex = flex
            // учитывается выделение, сделанное пользователем (грид в фокусе); автовыбор wijmo при загрузке данных граф не затемняет
            this._selH = (f) => {
                try {
                    if (!f.hostElement || !f.hostElement.contains(document.activeElement)) return
                    const r = f.selection?.row; const it = r >= 0 ? f.rows[r]?.dataItem : null
                    this.selectedId = it && it.id != null ? String(it.id) : ''
                } catch (e) { /* грид разрушен */ }
            }
            try { flex.selectionChanged.addHandler(this._selH) } catch (e) { this._flex = null }
        },
        unhookGrid() {
            if (this._flex && this._selH) try { this._flex.selectionChanged.removeHandler(this._selH) } catch (e) { /* грид разрушен */ }
            this._flex = null; this._selH = null
        },
    },
}
</script>

<style>
/* IE 2026 10 16 / ODOO-5800 (поручение Игоря 16.10): граф переходов — цвета рёбер как у кнопок переходов (вперёд primary, назад back, cancel danger), тема — токены */
.attr-trgraph { position: relative; padding: 4px 6px 2px; --trg-primary: var(--primary-color, #3b82f6); --trg-back: var(--text-color-secondary, #6b7280); --trg-danger: var(--red-500, #ef4444); --trg-review: var(--orange-500, #f59e0b); --trg-plain: var(--text-color-secondary, #6b7280); }
.trgraph-head { display: flex; align-items: baseline; gap: 12px; flex-wrap: wrap; font-size: 12px; color: var(--text-color-secondary); margin-bottom: 2px; }
.trgraph-title { font-weight: 600; color: var(--text-color); }
.trgraph-legend { display: inline-flex; gap: 10px; }
.trgraph-lg::before { content: ''; display: inline-block; width: 14px; height: 0; border-top: 2px solid currentColor; margin-right: 4px; vertical-align: middle; }
.trgraph-lg.trg-primary { color: var(--trg-primary); } .trgraph-lg.trg-back { color: var(--trg-back); } .trgraph-lg.trg-danger { color: var(--trg-danger); }
.trgraph-lg.trg-system::before { border-top-style: dashed; } .trgraph-lg.trg-system { color: var(--text-color-secondary); }
.trgraph-hint { margin-left: auto; opacity: .75; }
/* IE 2026 10 17 / ODOO-5800 (graph layout): кнопка «Default layout» в шапке и тяга узлов */
.trgraph-btn { display: inline-flex; align-items: center; gap: 4px; padding: 1px 8px; font-size: 11.5px; line-height: 16px; color: var(--text-color, #2b3a4a); background: var(--surface-card, #fff); border: 1px solid var(--surface-border, #b9c8dd); border-radius: 4px; cursor: pointer; }
.trgraph-btn:hover { background: var(--surface-hover, #eef3fb); border-color: var(--primary-color, #647eff); }
.trgraph-btn:disabled { opacity: .5; cursor: default; }
.trgraph-node { cursor: grab; touch-action: none; user-select: none; }
.trgraph-node.trg-drag { cursor: grabbing; }
.trgraph-node.trg-drag rect { stroke: var(--trg-primary); stroke-width: 2; filter: drop-shadow(0 2px 3px rgba(0,0,0,.2)); }
.trgraph-svg.trg-dragging .trgraph-edge { pointer-events: none; }
.trgraph-empty { font-size: 12px; color: var(--text-color-secondary); padding: 6px 2px; }
.trgraph-svg { display: block; width: 100%; overflow: visible; font-size: 11px; }
.trgraph-node rect { fill: var(--surface-card, #fff); stroke: var(--surface-border, #cbd5e1); stroke-width: 1.2; }
.trgraph-node text { fill: var(--text-color, #334155); font-size: 11.5px; }
.trgraph-node.trg-side rect { stroke-dasharray: 3 2; }
.trgraph-node.trg-hl rect { stroke: var(--trg-primary); stroke-width: 2; }
.trgraph-node.trg-level rect { stroke: var(--trg-review); }
.trgraph-node .trg-node-sub { font-size: 10.5px; fill: var(--text-color-secondary, #6b7280); }
.trgraph-edge { cursor: pointer; color: var(--trg-plain); }
.trgraph-edge.trg-primary { color: var(--trg-primary); } .trgraph-edge.trg-back { color: var(--trg-back); } .trgraph-edge.trg-danger { color: var(--trg-danger); } .trgraph-edge.trg-review { color: var(--trg-review); }
.trgraph-edge .trg-hit { fill: none; stroke: transparent; stroke-width: 12; }
.trgraph-edge .trg-line { fill: none; stroke: currentColor; stroke-width: 1.6; }
.trgraph-edge .trg-arrow { fill: currentColor; stroke: none; }
.trgraph-edge .trg-label { pointer-events: all; fill: currentColor; font-size: 10.5px; paint-order: stroke; stroke: var(--surface-card, #fff); stroke-width: 3px; stroke-linejoin: round; }
.trgraph-edge.trg-system .trg-line { stroke-dasharray: 4 3; color: var(--text-color-secondary); }
.trgraph-edge.trg-system { color: var(--text-color-secondary); }
.trgraph-edge.trg-off { opacity: .4; }
.trgraph-edge.trg-dim { opacity: .35; }
.trgraph-edge.trg-dim.trg-off { opacity: .18; }
.trgraph-edge:hover { opacity: 1; }
.trgraph-edge:hover .trg-line, .trgraph-edge.trg-sel .trg-line { stroke-width: 3; }
.trgraph-edge.trg-sel .trg-label { font-weight: 700; }
.trgraph-tip { position: absolute; z-index: 8; max-width: 300px; padding: 6px 10px; border-radius: 6px; background: var(--surface-overlay, var(--surface-card, #fff)); color: var(--text-color); border: 1px solid var(--surface-border, #cbd5e1); box-shadow: 0 4px 14px rgba(0,0,0,.18); font-size: 12px; pointer-events: none; }
.trgraph-tip-title { font-weight: 600; margin-bottom: 2px; }
.trgraph-tip-row { line-height: 1.35; } .trgraph-tip-k { color: var(--text-color-secondary); } .trgraph-tip-muted { color: var(--text-color-secondary); font-style: italic; }
</style>
